import { NgRedux, NgReduxModule } from '@angular-redux/store';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import arAE from '@angular/common/locales/ar-AE';
import deDE from '@angular/common/locales/de';

import enUS from '@angular/common/locales/en';
import enGB from '@angular/common/locales/en-GB';
import esUS from '@angular/common/locales/es-US';
import frFR from '@angular/common/locales/fr';
import idID from '@angular/common/locales/id'; // Indonesian
import itIT from '@angular/common/locales/it';
import jaJP from '@angular/common/locales/ja';
import koKR from '@angular/common/locales/ko';
import nlNL from '@angular/common/locales/nl';
import ptPT from '@angular/common/locales/pt-PT';
import ruRU from '@angular/common/locales/ru';
import thTh from '@angular/common/locales/th'; // Thai
import trTR from '@angular/common/locales/tr';
import zhCN from '@angular/common/locales/zh-Hans'; // Simplified Chinese
import zhTW from '@angular/common/locales/zh-Hant'; // Traditional Chinese
import { LOCALE_ID, NgModule } from '@angular/core';
import { Http, HttpModule, RequestOptions, XHRBackend } from '@angular/http';
import { BrowserModule } from '@angular/platform-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ClickOutsideModule } from 'ng-click-outside';
import { Ng2DeviceDetectorModule } from 'ng2-device-detector';
import { SimpleTimer } from 'ng2-simple-timer';
import { CookieService } from 'ngx-cookie-service';
import { Ng2Webstorage } from 'ngx-webstorage';
import { APP_CONFIG, getAppConfig, getLocale } from './api/config';
import {
  AccountStayPreferencesDao,
  AssociationDao,
  AvailabilityV2Dao,
  BaseDao,
  EmailUsageDao,
  EnrollmentDao,
  FeatureTogglesDao,
  HashDao,
  HasherDao,
  HotelDao,
  HotelExtraInfoDao,
  LoggingDao,
  MemberDao,
  PointsEstimationDao,
  RateRuleDao,
  ReservationDao,
  ReservationRateRulesDao,
  ReservationStayPreferencesDao,
  ReservationV2Dao,
  StayDao,
  UserStatusDao,
  TargetSystemDao
} from './api/daos';
import { CancelReservationDelegate, ManageStayDelegate, ModifyAccountDelegate, ModifyReservationDelegate } from './api/delegates';
import { AppActionsDelegate } from './api/delegates/app-actions.delegate';

import { DefaultHttpInterceptor } from './api/interceptors/default-http.interceptor';
import {
  AccountStayPreferencesService,
  AppConfigService,
  FeatureFlagsService,
  HasherService,
  HotelService,
  LogService,
  MemberService,
  ReservationAvailabilityService,
  ReservationService,
  ReservationStayPreferencesService,
  ShoppingCartService,
  StayService,
  TrackingJsonService,
  TranslationService,
  UHFInteractionService,
  UserService,
  TargetSystemService
} from './api/services';
import { PointsEstimateService } from './api/services/points-estimate.service';
import { RBFInteractionService } from './api/services/rbf-interaction.service';
import { ViewScrollService } from './api/services/view-scroll.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { ApplicationLocale } from './common/model/application-locale.model';

import { WindowRef } from './common/window.provider';
import { ComponentsModule } from './components';
import { httpFactory } from './http.factory';
import { CancelConfModule, CancelResModule, ManageStayModule, ModifyResModule, StayPrefModule, SubmitReviewModule, WriteReviewModule } from './routes';
import { Actions, IAppState, store } from './store';

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    ClickOutsideModule,
    NgbModule.forRoot(),
    Ng2Webstorage.forRoot({prefix: '', separator: '', caseSensitive: true}),
    AppRoutingModule,
    NgReduxModule,
    HttpModule,
    CommonModule,
    Ng2DeviceDetectorModule.forRoot(),
    HttpClientModule,
    CancelResModule,
    CancelConfModule,
    ModifyResModule,
    ManageStayModule,
    StayPrefModule,
    ComponentsModule,
    WriteReviewModule,
    SubmitReviewModule
  ],
  providers: [
    Actions,
    {provide: HTTP_INTERCEPTORS, useClass: DefaultHttpInterceptor, multi: true},
    HttpClient,
    {
      provide: Http,
      useFactory: httpFactory,
      deps: [XHRBackend, RequestOptions]
    },
    {
      provide: APP_CONFIG,
      useFactory: getAppConfig,
      deps: [AppConfigService]
    },
    {
      provide: LOCALE_ID,
      useFactory: getLocale,
      deps: [AppConfigService]
    },
    DatePipe,
    BaseDao,
    LoggingDao,
    ReservationDao,
    ReservationV2Dao,
    FeatureTogglesDao,
    HotelDao,
    MemberDao,
    UserStatusDao,
    HotelExtraInfoDao,
    ReservationRateRulesDao,
    ReservationStayPreferencesDao,
    AccountStayPreferencesDao,
    RateRuleDao,
    AvailabilityV2Dao,
    PointsEstimationDao,
    AssociationDao,
    EnrollmentDao,
    HasherDao,
    HashDao,
    StayDao,
    EmailUsageDao,
    TargetSystemDao,
    AppActionsDelegate,
    CancelReservationDelegate,
    ModifyReservationDelegate,
    ManageStayDelegate,
    ModifyAccountDelegate,
    AppConfigService,
    WindowRef,
    CookieService,
    LogService,
    SimpleTimer,
    TrackingJsonService,
    UserService,
    TranslationService,
    FeatureFlagsService,
    ShoppingCartService,
    ReservationService,
    AccountStayPreferencesService,
    ReservationAvailabilityService,
    ReservationStayPreferencesService,
    HotelService,
    MemberService,
    RBFInteractionService,
    PointsEstimateService,
    UHFInteractionService,
    ViewScrollService,
    HasherService,
    StayService,
    TargetSystemService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {

  constructor(ngRedux: NgRedux<IAppState>) {

    ngRedux.provideStore(store);

    registerLocaleData(enUS, ApplicationLocale.en_US);
    registerLocaleData(enGB, ApplicationLocale.en_GB);
    registerLocaleData(frFR, ApplicationLocale.fr_FR);
    registerLocaleData(itIT, ApplicationLocale.it_IT);
    registerLocaleData(ptPT, ApplicationLocale.pt_PT);
    registerLocaleData(esUS, ApplicationLocale.es_US);
    registerLocaleData(arAE, ApplicationLocale.ar_AE);
    registerLocaleData(zhTW, ApplicationLocale.zh_TW);
    registerLocaleData(zhCN, ApplicationLocale.zh_CN);
    registerLocaleData(deDE, ApplicationLocale.de_DE);
    registerLocaleData(nlNL, ApplicationLocale.nl_NL);
    registerLocaleData(idID, ApplicationLocale.id_ID);
    registerLocaleData(jaJP, ApplicationLocale.ja_JP);
    registerLocaleData(koKR, ApplicationLocale.ko_KR);
    registerLocaleData(ruRU, ApplicationLocale.ru_RU);
    registerLocaleData(thTh, ApplicationLocale.th_TH);
    registerLocaleData(trTR, ApplicationLocale.tr_TR);
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/app.module.ts